import { useState } from 'react'
import {
  FaArrowRight,
  FaCheckCircle,
  FaChevronDown,
  FaTruck,
  FaShippingFast,
  FaSnowflake,
  FaIndustry,
  FaMapMarkerAlt,
  FaBolt,
  FaShieldAlt,
  FaHeadset,
  FaClock,
} from 'react-icons/fa'
import { Link } from 'react-router-dom'
import SEO from '../../lib/SEO'
import styles from './home.module.scss'

import heroBg from '../../assets/img/trucks/truck4.jpg'
import fleetImg from '../../assets/img/trucks/img1.jpeg'

const Home = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const faqs = [
    {
      q: 'What states do you operate in?',
      a: 'Vision International covers all 48 contiguous states. Our primary corridors are Midwest–East Coast, Midwest–Southeast, and Midwest–Southwest.',
    },
    {
      q: 'How quickly can you pick up my freight?',
      a: 'Standard FTL and LTL pickups are scheduled within 24 hours. For expedited shipments, we guarantee pickup within 2 hours of booking confirmation.',
    },
    {
      q: 'Do you offer real-time tracking?',
      a: 'Yes — every load is GPS-tracked and you receive automated updates at pickup, in-transit, and delivery. Our online portal and mobile app give you live visibility 24/7.',
    },
    {
      q: 'What types of cargo do you haul?',
      a: 'Dry van, refrigerated, flatbed, oversized, hazardous materials, and high-value cargo. We handle virtually any freight category with the right equipment for the job.',
    },
    {
      q: 'Are your drivers CDL-A certified?',
      a: 'All Vision International drivers hold CDL-A licenses with clean MVRs, pass drug screenings, and are ELD-compliant. Our FMCSA safety rating is Satisfactory.',
    },
  ]

  return (
    <>
      <SEO
        title='Vision International – Freight Without Limits'
        description='Vision International LLC provides full truckload, LTL, expedited, and refrigerated freight across all 48 states. Call 24/7 dispatch or get a free quote online.'
        keywords='trucking company USA, freight transportation, FTL, LTL, expedited shipping, refrigerated transport, Vision International logistics'
        canonical='https://vi4u.net/'
      />

      <div className={styles.page}>
        {/* ═══════════ HERO ═══════════ */}
        <section
          className={styles.hero}
          style={{ backgroundImage: `url(${heroBg})` }}
        >
          <div className={styles.heroOverlay} />
          <div className={styles.heroContent}>
            <span className={styles.heroBadge}>Nationwide Carrier · Est. 2015</span>
            <h1 className={styles.heroTitle}>
              <span>FREIGHT</span>
              <span>WITHOUT</span>
              <span className={styles.heroYellow}>
                LIMITS<span className={styles.dot}>.</span>
              </span>
            </h1>
            <p className={styles.heroSub}>
              Vision International delivers your cargo — 48 states, any load size. We show up on
              time, every time.
            </p>
            <div className={styles.heroCta}>
              <Link
                to='/contact-us'
                className={styles.btnPrimary}
              >
                Get a Free Quote <FaArrowRight />
              </Link>
              <Link
                to='/services'
                className={styles.btnSecondary}
              >
                Our Services
              </Link>
            </div>
            <div className={styles.heroBadges}>
              <span>
                <FaCheckCircle /> DOT Certified
              </span>
              <span>
                <FaCheckCircle /> 24/7 Dispatch
              </span>
              <span>
                <FaCheckCircle /> GPS Tracking
              </span>
              <span>
                <FaCheckCircle /> HAZMAT Ready
              </span>
            </div>
          </div>
        </section>

        {/* ═══════════ STATS ═══════════ */}
        <section className={styles.stats}>
          <div className={styles.statsInner}>
            {[
              { n: '15+', l: 'Years on the Road' },
              { n: '48', l: 'States Covered' },
              { n: '24/7', l: 'Dispatch Available' },
              { n: '99.1%', l: 'On-Time Delivery' },
            ].map((s) => (
              <div
                key={s.l}
                className={styles.stat}
              >
                <span className={styles.statN}>{s.n}</span>
                <span className={styles.statL}>{s.l}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════ SERVICES ═══════════ */}
        <section className={styles.services}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionLabel}>What We Haul</div>
            <h2 className={styles.sectionTitle}>
              Every Load. <span className={styles.yellow}>Every Route.</span>
            </h2>
            <p className={styles.sectionSub}>
              From a single pallet to a full fleet of dedicated trucks — we have the right solution.
            </p>

            <div className={styles.servicesGrid}>
              {[
                {
                  icon: <FaTruck />,
                  title: 'Full Truckload (FTL)',
                  desc: 'Dedicated 53-ft trailers, direct routes, fastest transit. Perfect for large shipments that need a truck all to themselves.',
                },
                {
                  icon: <FaShippingFast />,
                  title: 'Less-Than-Truckload (LTL)',
                  desc: 'Share trailer space, pay only for what you use. Reliable consolidated freight for shipments under 10,000 lbs.',
                },
                {
                  icon: <FaBolt />,
                  title: 'Expedited Freight',
                  desc: 'Team drivers, non-stop runs, guaranteed pickup in 2 hours. When your deadline is not negotiable.',
                },
                {
                  icon: <FaSnowflake />,
                  title: 'Refrigerated / Reefer',
                  desc: 'Temperature-controlled transport from -20°F to +70°F. FSMA compliant. Continuous digital monitoring.',
                },
                {
                  icon: <FaIndustry />,
                  title: 'Flatbed & Specialized',
                  desc: 'Flatbed, step-deck, lowboy, RGN. Heavy machinery, oversized loads, construction equipment — we handle it.',
                },
                {
                  icon: <FaMapMarkerAlt />,
                  title: 'Specialized Cargo',
                  desc: "Oversized loads, heavy haul, high-value freight. Step-deck, lowboy, RGN — we handle what others won't.",
                },
              ].map((s) => (
                <div
                  key={s.title}
                  className={styles.serviceCard}
                >
                  <div className={styles.serviceIcon}>{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>

            <div className={styles.servicesCta}>
              <Link
                to='/services'
                className={styles.btnPrimary}
              >
                View All Services <FaArrowRight />
              </Link>
            </div>
          </div>
        </section>

        {/* ═══════════ FLEET PHOTO SECTION ═══════════ */}
        <section
          className={styles.fleetSection}
          style={{ backgroundImage: `url(${fleetImg})` }}
        >
          <div className={styles.fleetOverlay} />
          <div className={styles.fleetContent}>
            <div className={styles.sectionLabel}>Our Fleet</div>
            <h2 className={styles.fleetTitle}>
              Modern fleet.
              <br />
              <span className={styles.yellow}>Zero downtime.</span>
            </h2>
            <p>
              Late-model Freightliner Cascadias and Kenworth T680s — inspected weekly, maintained to
              exceed FMCSA standards.
            </p>
            <ul className={styles.fleetList}>
              <li>
                <FaCheckCircle /> 53-ft dry van trailers
              </li>
              <li>
                <FaCheckCircle /> Multi-temp reefer units
              </li>
              <li>
                <FaCheckCircle /> Flatbed & step-deck
              </li>
              <li>
                <FaCheckCircle /> ELD-equipped & GPS tracked
              </li>
            </ul>
            <Link
              to='/contact-us'
              className={styles.btnPrimary}
            >
              Get a Quote <FaArrowRight />
            </Link>
          </div>
        </section>

        {/* ═══════════ WHY US ═══════════ */}
        <section className={styles.why}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionLabel}>Why Vision International</div>
            <h2 className={styles.sectionTitle}>
              Built on <span className={styles.yellow}>reliability.</span>
            </h2>
            <div className={styles.whyGrid}>
              {[
                {
                  icon: <FaClock />,
                  title: '24/7 Dispatch',
                  desc: 'A live dispatcher picks up — every call, every hour, every day of the year.',
                },
                {
                  icon: <FaShieldAlt />,
                  title: 'Safety First',
                  desc: 'Spotless FMCSA record. Every driver CDL-A, ELD compliant, drug-tested.',
                },
                {
                  icon: <FaMapMarkerAlt />,
                  title: 'Live Tracking',
                  desc: 'Real-time GPS on every load. Portal + mobile app + proactive updates.',
                },
                {
                  icon: <FaHeadset />,
                  title: 'Dedicated Account Manager',
                  desc: 'One person, one number. No call centers, no voicemail hell.',
                },
              ].map((w) => (
                <div
                  key={w.title}
                  className={styles.whyCard}
                >
                  <div className={styles.whyIcon}>{w.icon}</div>
                  <h3>{w.title}</h3>
                  <p>{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════ CTA BANNER ═══════════ */}
        <section className={styles.ctaBanner}>
          <div className={styles.ctaBannerInner}>
            <h2>Ready to move your freight?</h2>
            <p>Get a quote in 30 minutes. Our team is standing by right now.</p>
            <div className={styles.ctaBannerButtons}>
              <Link
                to='/contact-us'
                className={styles.btnDark}
              >
                Request a Quote <FaArrowRight />
              </Link>
              <a
                href='tel:+16305042688'
                className={styles.btnOutlineDark}
              >
                Call +1 (630) 504-2688
              </a>
            </div>
          </div>
        </section>

        {/* ═══════════ FAQ ═══════════ */}
        <section className={styles.faq}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionLabel}>FAQ</div>
            <h2 className={styles.sectionTitle}>Common Questions</h2>
            <div className={styles.faqList}>
              {faqs.map((f, i) => (
                <div
                  key={i}
                  className={`${styles.faqItem} ${openFaq === i ? styles.faqOpen : ''}`}
                >
                  <button
                    className={styles.faqQ}
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span>{f.q}</span>
                    <FaChevronDown className={styles.faqChev} />
                  </button>
                  {openFaq === i && (
                    <div className={styles.faqA}>
                      <p>{f.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Home
